import { PROFITABILITY_SORT_FIELDS, PROJECT_STATUSES, UTILISATION_SORT_FIELDS } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'
import { REPORT_DEFAULT_PAGE_SIZE, REPORT_PAGE_SIZES } from './operational-search'

/**
 * Reports › Projects keep their filters in the URL, like the other reports: shareable and back-button
 * safe. An invalid value falls back to the default, and defaults stay out of the URL.
 */
const q = z.string().trim().max(200).optional().catch(undefined)
const uuid = z.uuid().optional().catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
type ReportPageSize = (typeof REPORT_PAGE_SIZES)[number]
const pageSize = z
  .number()
  .refine((n): n is ReportPageSize => (REPORT_PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)
const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/** The status filter: one status, or ALL (closed and cancelled too); absent: the open projects. */
export const PROJECT_REPORT_STATUSES = [...PROJECT_STATUSES, 'ALL'] as const
export type ProjectReportStatus = (typeof PROJECT_REPORT_STATUSES)[number]

function sortOf<const F extends string>(fields: readonly F[], defaultSort: F | `-${F}`) {
  const valid = (value: string): value is F | `-${F}` =>
    (fields as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
  return { valid, default: defaultSort, schema: z.string().refine(valid).optional().catch(undefined) }
}

/** Profitability sorts by the contract's fields; the page asks for the code order by default. */
export const PROFITABILITY_SORT = sortOf(PROFITABILITY_SORT_FIELDS, 'code')
/** Budget against actual and unbilled work: the API's sort keys and default orders (I1). */
export const BUDGET_VS_ACTUAL_SORT = sortOf(
  ['code', 'budget', 'committed', 'actual', 'remaining', 'usePct'],
  'code',
)
export const UNBILLED_SORT = sortOf(['code', 'readyAmount', 'tmAmount', 'total', 'readySince'], '-total')
/** Utilisation (P6): by name unless another order is chosen. */
export const UTILISATION_SORT = sortOf(UTILISATION_SORT_FIELDS, 'name')

const common = {
  q,
  status: z.enum(PROJECT_REPORT_STATUSES).optional().catch(undefined),
  customerId: uuid,
  page,
  pageSize,
}

export const profitabilitySearchSchema = z.object({ ...common, sort: PROFITABILITY_SORT.schema })
export type ProfitabilitySearch = z.infer<typeof profitabilitySearchSchema>

export const budgetVsActualSearchSchema = z.object({
  ...common,
  sort: BUDGET_VS_ACTUAL_SORT.schema,
  asOf: isoDate,
  projectId: uuid,
  costCategoryId: uuid,
  overOnly: z.boolean().optional().catch(undefined),
})
export type BudgetVsActualSearch = z.infer<typeof budgetVsActualSearchSchema>

export const unbilledSearchSchema = z.object({ ...common, sort: UNBILLED_SORT.schema })
export type UnbilledSearch = z.infer<typeof unbilledSearchSchema>

/** Utilisation (P6): the period (this month when absent), org unit, project, person, sort and page. */
export const utilisationSearchSchema = z.object({
  from: isoDate,
  to: isoDate,
  orgUnitId: uuid,
  projectId: uuid,
  userId: uuid,
  sort: UTILISATION_SORT.schema,
  page,
  pageSize,
})
export type UtilisationSearch = z.infer<typeof utilisationSearchSchema>

export type ProjectReportSearch = ProfitabilitySearch | BudgetVsActualSearch | UnbilledSearch

/** The API's filters from the URL's: the open projects unless a status (or ALL) is chosen. */
export function projectReportFilters(search: {
  q?: string | undefined
  status?: ProjectReportStatus | undefined
  customerId?: string | undefined
  page?: number | undefined
  pageSize?: number | undefined
  sort?: string | undefined
}) {
  return {
    q: search.q || undefined,
    status: search.status && search.status !== 'ALL' ? search.status : undefined,
    includeClosed: search.status === 'ALL' || undefined,
    customerId: search.customerId,
    sort: search.sort,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? REPORT_DEFAULT_PAGE_SIZE,
  }
}
